import React, { useState, useRef } from 'react';
import { Upload, Loader2 } from 'lucide-react';
import { uploadFile } from '@/lib/api';
import { toast } from 'sonner';

const ACCEPTED = '.jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv';

const DropZone = ({ onFilesUploaded, uploading, setUploading }) => {
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const processFiles = async (files) => {
    if (files.length === 0) return;
    setUploading(true);
    const results = [];
    try {
      for (const file of files) {
        const ext = file.name.split('.').pop().toLowerCase();
        const allowed = ACCEPTED.replace(/\./g, '').split(',');
        if (!allowed.includes(ext)) {
          toast.error(`Tipo no permitido: .${ext}`);
          continue;
        }
        const response = await uploadFile(file);
        results.push({ url: response.data.url, filename: response.data.filename });
      }
      if (results.length > 0) {
        onFilesUploaded(results);
        toast.success(`${results.length} archivo(s) subido(s)`);
      }
    } catch (error) {
      console.error('Error uploading:', error);
      toast.error('Error al subir archivos');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    processFiles(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleInputChange = (e) => {
    processFiles(Array.from(e.target.files));
    e.target.value = '';
  };

  return (
    <div
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onClick={handleClick}
      data-testid="drop-zone"
      className={`
        relative flex flex-col items-center justify-center gap-2 p-5 rounded-lg border-2 border-dashed cursor-pointer
        transition-colors duration-200
        ${dragOver
          ? 'border-emerald-500 bg-emerald-50'
          : 'border-stone-300 hover:border-stone-400 bg-stone-50 hover:bg-stone-100'
        }
        ${uploading ? 'pointer-events-none opacity-60' : ''}
      `}
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPTED}
        className="hidden"
        onChange={handleInputChange}
      />
      {uploading ? (
        <Loader2 className="w-6 h-6 text-stone-400 animate-spin" />
      ) : (
        <Upload className={`w-6 h-6 ${dragOver ? 'text-emerald-600' : 'text-stone-400'}`} />
      )}
      <p className={`text-sm font-medium ${dragOver ? 'text-emerald-700' : 'text-stone-500'}`}>
        {uploading ? 'Subiendo...' : dragOver ? 'Suelta aqui' : 'Arrastra archivos o haz clic'}
      </p>
      <p className="text-xs text-stone-400">Imagenes, PDF, documentos (max 10MB)</p>
    </div>
  );
};

export default DropZone;
